import clsx from 'clsx';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

import Spinner from '@dashboard/components/Spinner';
import Button from '@dashboard/components/Button';

const FormSubmit = ({
  children,
  isInline,
  align = 'start',
  direction = 'row',
  hasMargin = true,
}) => {
  return (
    <div
      className={clsx('flex items-baseline gap-4', {
        'flex-row justify-between': isInline,
        'justify-start': align === 'start',
        'justify-end': align === 'end',
        'flex-col': direction === 'column',
        'flex-row': direction === 'row',
        'mt-4': hasMargin,
      })}
    >
      {children}
    </div>
  );
};

const FORM_SUBMIT_STATES = {
  INITIAL: 'initial',
  SUBMITTING: 'submitting',
  SUBMITTED: 'submitted',
};

// initial, submitting, submitted, error
const FormSubmitButton = ({
  children,
  appearance = 'primary',
  size = 'base',
  onClick,
  type = 'submit',
  submitSpinner = true,
  disabled = false,
  arrowRight = false,
}) => {
  const { errors, isSubmitting, isValid, isValidating } = useFormikContext();
  const [state, setState] = useState(FORM_SUBMIT_STATES.INITIAL);

  useEffect(() => {
    if (state === FORM_SUBMIT_STATES.INITIAL && isSubmitting && isValid) {
      return setState(FORM_SUBMIT_STATES.SUBMITTING);
    }

    if (state === FORM_SUBMIT_STATES.SUBMITTING && !isSubmitting && isEmpty(errors)) {
      setState(FORM_SUBMIT_STATES.SUBMITTED);

      window.setTimeout(() => {
        setState(FORM_SUBMIT_STATES.INITIAL);
      }, 2000);

      return;
    }

    if (state === FORM_SUBMIT_STATES.SUBMITTING && !isSubmitting) {
      setState(FORM_SUBMIT_STATES.INITIAL);
    }
  }, [errors, isSubmitting, isValidating, state]);

  const isDisabled = disabled || state === FORM_SUBMIT_STATES.SUBMITTING || isValidating;

  return (
    <Button
      type={type}
      appearance={appearance}
      onClick={onClick}
      disabled={isDisabled}
      size={size}
      className="flex items-center"
    >
      {state === FORM_SUBMIT_STATES.INITIAL && (
        <>
          {children}

          {arrowRight && <ArrowRightIcon className="w-4 h-4 ml-2" />}
        </>
      )}

      {state === FORM_SUBMIT_STATES.SUBMITTING && (
        <>
          {children}

          {arrowRight && <ArrowRightIcon className="w-4 h-4 ml-2" />}

          {submitSpinner && <Spinner className="ml-2 h-5 w-5 !text-current" />}
        </>
      )}

      {state === FORM_SUBMIT_STATES.SUBMITTED && (
        <>
          {children}

          {arrowRight && <ArrowRightIcon className="w-4 h-4 ml-2" />}
        </>
      )}
    </Button>
  );
};

FormSubmit.Button = FormSubmitButton;

const FormSubmitState = ({ visible }) => {
  if (!visible) return '';

  return <div className="text-gray-600">Changes not saved</div>;
};

FormSubmit.State = FormSubmitState;

const FormSubmitCancel = ({
  disabled = false,
  onClick,
  appearance = 'secondary',
  size = 'base',
  children = 'Cancel',
}) => {
  return (
    <Button disabled={disabled} appearance={appearance} onClick={onClick} size={size}>
      {children}
    </Button>
  );
};

FormSubmit.Cancel = FormSubmitCancel;

export default FormSubmit;
